import { Avatar, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { GroupAvatar } from "@/components/settings/organization/groups/GroupAvatar";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useListGroups, useListRoleAssignments } from "@/queries/group-queries";
import { useMembers } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import type { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

type ResourceGroupAccessProps = {
    resourceType: ResourceType;
    resourceId: string;
    maxVisible?: number;
    className?: string;
    trackingPrefix?: string;
};

type AccessItem = 
    | { type: "group"; id: string; name: string }
    | { type: "user"; id: string; name: string; avatarUrl: string };

export const ResourceGroupAccess: FC<ResourceGroupAccessProps> = ({ 
    resourceType,
    resourceId, 
    maxVisible = 5, 
    className,
    trackingPrefix = "resource",
}) => {
    const navigate = useNavigate();
    const api = useGitpodAPI();
    const { data: currentUser } = useAuthenticatedUser();
    const { data: roleAssignmentsData, isLoading: isLoadingAssignments } = useListRoleAssignments({
        resourceType,
        resourceId,
    });

    const { data: groupsData, isLoading: isLoadingGroups } = useListGroups();
    const { data: membersData } = useMembers();

    const assignments = useMemo(
        () => roleAssignmentsData?.assignments ?? [],
        [roleAssignmentsData],
    );
    const groups = useMemo(
        () => groupsData?.groups ?? [],
        [groupsData],
    );

    // Get unique group IDs from assignments
    const groupIds = useMemo(
        () => new Set(assignments.map((a) => a.groupId)),
        [assignments],
    );

    // Find regular groups (non-direct-share) that have access
    const regularGroupsWithAccess = useMemo(
        () => groups.filter((g) => groupIds.has(g.id) && !g.directShare),
        [groups, groupIds],
    );

    // Find direct share groups
    const directShareGroups = useMemo(
        () => groups.filter((g) => groupIds.has(g.id) && g.directShare),
        [groups, groupIds],
    );

    // Create a stable key for the query based on direct share group IDs
    const directShareGroupIdsKey = useMemo(
        () => directShareGroups.map((g) => g.id).sort().join(","),
        [directShareGroups],
    );

    // Fetch memberships for direct share groups to get individual users
    const { data: directShareUsers } = useQuery({
        queryKey: keyWithPrincipal(["directShareUsers", resourceType, resourceId, directShareGroupIdsKey]),
        queryFn: async () => {
            if (directShareGroups.length === 0) return [];
            
            const userIds = new Set<string>();
            
            await Promise.all(
                directShareGroups.map(async (group) => {
                    const response = await api.groupService.listMemberships({
                        groupId: group.id,
                        pagination: { pageSize: 100 },
                    });
                    
                    for (const member of response.members) {
                        // Check if this is a user membership (not service account, runner, etc.)
                        if (member.subject?.principal === Principal.USER && member.subject.id) {
                            userIds.add(member.subject.id);
                        }
                    }
                }),
            );
            
            return Array.from(userIds);
        },
        enabled: directShareGroups.length > 0,
        staleTime: 30_000,
    });

    // Build combined list of access items (groups + users, excluding current user)
    const accessItems = useMemo(() => {
        const items: AccessItem[] = [];
        
        // Add regular groups
        for (const group of regularGroupsWithAccess) {
            items.push({ type: "group", id: group.id, name: group.name });
        }
        
        // Add direct share users (excluding current user)
        if (directShareUsers && membersData?.members) {
            for (const userId of directShareUsers) {
                // Skip the current user
                if (currentUser?.id === userId) continue;
                
                const member = membersData.members.find((m) => m.userId === userId);
                if (member) {
                    items.push({
                        type: "user",
                        id: member.userId,
                        name: member.fullName,
                        avatarUrl: member.avatarUrl,
                    });
                }
            }
        }
        
        return items;
    }, [regularGroupsWithAccess, directShareUsers, membersData, currentUser]);

    if (isLoadingAssignments || isLoadingGroups) {
        return null;
    }

    if (accessItems.length === 0) {
        return null;
    }

    const visibleItems = accessItems.slice(0, maxVisible);
    const remainingItems = accessItems.slice(maxVisible);
    const remainingCount = remainingItems.length;
    const showRemainingCount = remainingCount > 0;

    const handleGroupClick = (groupId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/settings/members/group/${groupId}`);
    };

    const handleUserClick = (userId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/settings/members?user=${userId}`);
    };

    const remainingItemsTooltip = (
        <div className="flex flex-col gap-1">
            {remainingItems.map((item) => (
                <button
                    key={`${item.type}-${item.id}`}
                    type="button"
                    onClick={(e) => 
                        item.type === "group" 
                            ? handleGroupClick(item.id, e) 
                            : handleUserClick(item.id, e)
                    }
                    className="text-left hover:underline cursor-pointer focus:outline-none focus:underline"
                    data-tracking-id={`${trackingPrefix}-${item.type}-access-remaining-${item.id}`}
                >
                    {item.name}
                </button>
            ))}
        </div>
    );

    return (
        <div className={cn("flex items-center", className)}>
            <ul className="group/groups flex items-center" aria-label="Groups and users with access">
                {visibleItems.map((item, index) => (
                    <li
                        key={`${item.type}-${item.id}`}
                        style={{ zIndex: maxVisible - index }}
                        className={cn(
                            "relative flex-shrink-0 transition-all ease-out",
                            index > 0 && "relative -ml-2 group-hover/groups:-ml-1",
                        )}
                    >
                        <Tooltip content={item.name} usePortal>
                            {item.type === "group" ? (
                                <button
                                    type="button"
                                    onClick={(e) => handleGroupClick(item.id, e)}
                                    className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg"
                                    aria-label={`View ${item.name} details`}
                                    data-tracking-id={`${trackingPrefix}-group-access-${item.id}`}
                                >
                                    <GroupAvatar
                                        groupName={item.name}
                                        size="sm"
                                        className="rounded-lg border-2 border-surface-glass ring-1 ring-border-light transition-shadow group-hover/groups:shadow-sm hover:shadow-md"
                                    />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={(e) => handleUserClick(item.id, e)}
                                    className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full"
                                    aria-label={`View ${item.name} details`}
                                    data-tracking-id={`${trackingPrefix}-user-access-${item.id}`}
                                >
                                    <Avatar size={24} className="size-6 rounded-full border-2 border-surface-glass ring-1 ring-border-light transition-shadow group-hover/groups:shadow-sm hover:shadow-md">
                                        <AvatarImage src={item.avatarUrl} alt={item.name} />
                                        <AvatarInitials name={item.name} size={24} />
                                    </Avatar>
                                </button>
                            )}
                        </Tooltip>
                    </li>
                ))}

                {showRemainingCount && (
                    <li className="relative -ml-2 flex-shrink-0">
                        <Tooltip content={remainingItemsTooltip} usePortal>
                            <div
                                className={cn(
                                    "flex size-6 items-center justify-center rounded-lg border-2 border-surface-glass bg-surface-secondary text-xs font-medium text-content-secondary ring-1 ring-border-light cursor-default",
                                )}
                                aria-label={`${remainingCount} more`}
                            >
                                +{remainingCount}
                            </div>
                        </Tooltip>
                    </li>
                )}
            </ul>
        </div>
    );
};
